import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    Typography,
    Box,
    CircularProgress,
    Alert,
    Paper,
    InputAdornment
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import PhoneIcon from '@mui/icons-material/Phone';
import BusinessIcon from '@mui/icons-material/Business';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const SERVICE_AMOUNT = 2000; // ₹2,000 for adding phone number

const FormSection = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
    borderRadius: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
    fontWeight: 600,
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
}));

const AddPhoneToGmbForm = ({ open, onClose }) => {
    const theme = useTheme();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    const [formData, setFormData] = useState({
        business_name: '',
        gmb_profile_url: '',
        current_phone: '',
        new_phone: '',
        contact_name: '',
        contact_email: '',
        contact_phone: '',
        notes: ''
    });

    const [errors, setErrors] = useState({});

    const handleChange = (field) => (e) => {
        setFormData(prev => ({ ...prev, [field]: e.target.value }));
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: null }));
        }
    };

    const validate = () => {
        const newErrors = {};
        if (!formData.business_name?.trim()) newErrors.business_name = 'Business name is required';
        if (!formData.new_phone?.trim()) newErrors.new_phone = 'New phone number is required';
        if (!formData.contact_name?.trim()) newErrors.contact_name = 'Contact name is required';
        if (!formData.contact_email?.trim()) newErrors.contact_email = 'Email is required';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contact_email)) newErrors.contact_email = 'Invalid email';
        if (!formData.contact_phone?.trim()) newErrors.contact_phone = 'Contact phone is required';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;

        setLoading(true);
        setError(null);

        try {
            const apiBase = process.env.REACT_APP_API_BASE ||
                (window.location.hostname === 'localhost' ? 'http://localhost:5000' : '');

            const response = await fetch(`${apiBase}/api/v1/customer/gmb-phone-request/create`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ...formData,
                    request_type: 'add_phone',
                    amount: SERVICE_AMOUNT
                })
            });

            const result = await response.json();

            if (!result.success) {
                throw new Error(result.message || 'Failed to submit request');
            }

            // For now, just show success without payment
            // Payment can be added later similar to GmbCreationForm
            setSuccess(true);

        } catch (err) {
            console.error('Add Phone request error:', err);
            setError(err.message || 'Failed to submit request');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        if (!loading) {
            setFormData({
                business_name: '',
                gmb_profile_url: '',
                current_phone: '',
                new_phone: '',
                contact_name: '',
                contact_email: '',
                contact_phone: '',
                notes: ''
            });
            setErrors({});
            setError(null);
            setSuccess(false);
            onClose && onClose({ success });
        }
    };

    const renderSuccess = () => (
        <Box textAlign="center" py={4}>
            <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
            <Typography variant="h5" fontWeight={600} gutterBottom>
                Request Submitted!
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
                Your request to add a phone number has been submitted.
            </Typography>
            <Typography variant="body2" color="text.secondary">
                We'll contact you at <strong>{formData.contact_email}</strong> to proceed.
            </Typography>
        </Box>
    );

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: { borderRadius: 3 }
            }}
        >
            <DialogTitle sx={{ pb: 0 }}>
                <Typography variant="h5" component="span" fontWeight={700} sx={{ display: 'block' }}>
                    {success ? '🎉 Request Submitted!' : "Add Phone Number to GMB"}
                </Typography>
                {!success && (
                    <Typography variant="body2" color="text.secondary">
                        Request to add or update phone number on your GMB profile
                    </Typography>
                )}
            </DialogTitle>

            <DialogContent sx={{ pt: 3 }}>
                {success ? renderSuccess() : (
                    <>
                        {error && (
                            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                                {error}
                            </Alert>
                        )}

                        <FormSection elevation={0}>
                            <SectionTitle variant="h6">
                                <BusinessIcon /> Business Information
                            </SectionTitle>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Business Name"
                                        value={formData.business_name}
                                        onChange={handleChange('business_name')}
                                        error={!!errors.business_name}
                                        helperText={errors.business_name}
                                        required
                                        placeholder="Your business name as it appears on GMB"
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="GMB Profile URL (optional)"
                                        value={formData.gmb_profile_url}
                                        onChange={handleChange('gmb_profile_url')}
                                        placeholder="https://www.google.com/maps/place/..."
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Current Phone (if any)"
                                        value={formData.current_phone}
                                        onChange={handleChange('current_phone')}
                                        placeholder="Existing phone on GMB"
                                        InputProps={{
                                            startAdornment: <InputAdornment position="start">+91</InputAdornment>
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="New Phone Number"
                                        value={formData.new_phone}
                                        onChange={handleChange('new_phone')}
                                        error={!!errors.new_phone}
                                        helperText={errors.new_phone}
                                        required
                                        placeholder="Phone to add/update"
                                        InputProps={{
                                            startAdornment: <InputAdornment position="start">+91</InputAdornment>
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        </FormSection>

                        <FormSection elevation={0}>
                            <SectionTitle variant="h6">
                                <PhoneIcon /> Contact Information
                            </SectionTitle>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Your Name"
                                        value={formData.contact_name}
                                        onChange={handleChange('contact_name')}
                                        error={!!errors.contact_name}
                                        helperText={errors.contact_name}
                                        required
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Email"
                                        type="email"
                                        value={formData.contact_email}
                                        onChange={handleChange('contact_email')}
                                        error={!!errors.contact_email}
                                        helperText={errors.contact_email}
                                        required
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Contact Phone"
                                        value={formData.contact_phone}
                                        onChange={handleChange('contact_phone')}
                                        error={!!errors.contact_phone}
                                        helperText={errors.contact_phone}
                                        required
                                        InputProps={{
                                            startAdornment: <InputAdornment position="start">+91</InputAdornment>
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={2}
                                        label="Additional Notes (optional)"
                                        value={formData.notes}
                                        onChange={handleChange('notes')}
                                        placeholder="Any special instructions or details..."
                                    />
                                </Grid>
                            </Grid>
                        </FormSection>

                        <Paper
                            elevation={0}
                            sx={{
                                p: 2,
                                bgcolor: theme.palette.info.main + '10',
                                borderRadius: 2,
                                border: `1px solid ${theme.palette.info.main}`
                            }}
                        >
                            <Typography variant="body2" color="text.secondary">
                                Our team will review your request and contact you to verify ownership before making any changes.
                            </Typography>
                        </Paper>
                    </>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 3 }}>
                {success ? (
                    <Button onClick={handleClose} variant="contained" size="large">
                        Close
                    </Button>
                ) : (
                    <>
                        <Button onClick={handleClose} disabled={loading}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSubmit}
                            variant="contained"
                            disabled={loading}
                            startIcon={loading && <CircularProgress size={20} color="inherit" />}
                        >
                            {loading ? 'Submitting...' : 'Submit Request'}
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default AddPhoneToGmbForm;
